import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";

type OwnerPhone = {
    phone_number: string;
    is_primary?: boolean | null;
    is_whatsapp?: boolean | null;
};

type OwnerEmail = {
    email: string;
    is_primary?: boolean | null;
};

type OwnersAddressRow = Database["public"]["Tables"]["owners_addresses"]["Row"];

type OwnersAddressSelect = Pick<
    OwnersAddressRow,
    "id" | "owner_id" | "street" | "number" | "neighborhood" | "zip_code" | "is_primary"
> & {
    city?: {
        name?: string | null;
        state?: {
            code?: string | null;
        } | null;
    } | null;
};

export interface OwnerWithPetsRow {
    owner: {
        id: string;
        name: string;
        created_at: string;
        document?: string | null;
        phones?: OwnerPhone[] | null;
        emails?: OwnerEmail[] | null;
        primaryAddress?: string | null;
    };
    pet: {
        id: string;
        name: string;
        record_number: string | null;
        birth_date: string | null;
        created_at: string;
        photo?: {
            file_path: string;
        };
        breed?: {
            name: string;
        };
        species?: {
            name: string;
        };
        status?: {
            name: string;
        };
        death_date?: string | null;
    } | null;
    petCount: number;
}

export interface PaginatedOwnersWithPets {
    data: OwnerWithPetsRow[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
}

interface RpcResponseRow {
    owner_id: string;
    owner_name: string;
    owner_created_at: string;
    owner_document: string | null;
    owner_phones: OwnerPhone[] | null;
    owner_emails: OwnerEmail[] | null;
    pet_id: string | null;
    pet_name: string | null;
    pet_record_number: string | null;
    pet_birth_date: string | null;
    pet_created_at: string | null;
    pet_photo: { file_path: string } | null;
    pet_breed: { name: string } | null;
    pet_species: { name: string } | null;
    pet_status: { name: string } | null;
    pet_death_date: string | null;
    pet_count: number;
    total_count: number;
}

// Normalize search term for queryKey to avoid duplicate requests for equivalent input
const normalizedSearch = (s?: string) => (s?.trim() ?? "");

const formatOwnerAddress = (address: OwnersAddressSelect | null | undefined): string | null => {
    if (!address) return null;

    const line1 = [address.street, address.number].filter((value) => Boolean(value?.trim())).join(", ");
    const line2 = [address.neighborhood, address.city?.name, address.city?.state?.code, address.zip_code]
        .filter((value) => Boolean(value?.trim()))
        .join(" - ");

    const formatted = [line1, line2].filter(Boolean).join(" · ").trim();
    return formatted.length > 0 ? formatted : null;
};

// Listar owners com pets para a tela de Customers com paginação usando RPC function
export const usePetsWithOwners = (
    searchTerm?: string,
    page: number = 1,
    pageSize: number = 50,
    options?: { enabled?: boolean }
) => {
    const { data: customerId } = useCurrentClinicCustomerId();

    return useQuery<PaginatedOwnersWithPets>({
        queryKey: ["owners-with-pets", customerId, normalizedSearch(searchTerm), page, pageSize],
        enabled: (options?.enabled !== false) && !!customerId,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            if (!customerId) throw new Error("Nenhuma clínica selecionada");

            const offset = (page - 1) * pageSize;

            // Call RPC function with pagination parameters
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error } = await (supabase as any).rpc("get_owners_with_pets", {
                p_customer_id: customerId,
                p_search: searchTerm?.trim() || null,
                p_offset: offset,
                p_limit: pageSize,
            });

            if (error) throw error;

            const rpcRows = (data as RpcResponseRow[] | null | undefined) ?? [];
            const ownerIds = Array.from(new Set(rpcRows.map((row) => row.owner_id).filter(Boolean)));
            const ownerAddressByOwnerId = new Map<string, string | null>();

            if (ownerIds.length > 0) {
                const { data: addressesData, error: addressesError } = await supabase
                    .from("owners_addresses")
                    .select(
                        `
                        id,
                        owner_id,
                        street,
                        number,
                        neighborhood,
                        zip_code,
                        is_primary,
                        city:cities(name, state:states(code))
                        `,
                    )
                    .in("owner_id", ownerIds)
                    .order("is_primary", { ascending: false })
                    .order("created_at", { ascending: true });

                if (addressesError) throw addressesError;

                const addresses = (addressesData as OwnersAddressSelect[] | null | undefined) ?? [];
                for (const address of addresses) {
                    if (!ownerAddressByOwnerId.has(address.owner_id)) {
                        ownerAddressByOwnerId.set(address.owner_id, formatOwnerAddress(address));
                    }
                }
            }

            // Transform response
            const rows: OwnerWithPetsRow[] = rpcRows.map((row: RpcResponseRow) => ({
                owner: {
                    id: row.owner_id,
                    name: row.owner_name,
                    created_at: row.owner_created_at,
                    document: row.owner_document,
                    phones: row.owner_phones,
                    emails: row.owner_emails,
                    primaryAddress: ownerAddressByOwnerId.get(row.owner_id) ?? null,
                },
                pet: row.pet_id ? {
                    id: row.pet_id,
                    name: row.pet_name,
                    record_number: row.pet_record_number,
                    birth_date: row.pet_birth_date,
                    created_at: row.pet_created_at,
                    photo: row.pet_photo,
                    breed: row.pet_breed,
                    species: row.pet_species,
                    status: row.pet_status,
                    death_date: row.pet_death_date,
                } : null,
                petCount: Number(row.pet_count),
            }));

            const totalCount = rpcRows.length > 0 ? Number(rpcRows[0].total_count) : 0;
            const totalPages = Math.ceil(totalCount / pageSize);

            return {
                data: rows,
                totalCount,
                totalPages,
                currentPage: page,
                pageSize,
            };
        },
    });
};
